import { useMemo, useState } from "react"
import { HttpStatusCode } from "axios"
import { useMutation, UseMutationOptions, UseMutationResult } from "@tanstack/react-query"
import { formatErrorsFromResponse, getFirstError } from "@utils/helpers"
import { ErrorResponseBody, FormattedValidationError, ResponseBody } from "@/types"
import { useModal } from "./useModal"

type Props<Data, GetFirst extends boolean, Params = void, Context = unknown> = Omit<
  UseMutationOptions<ResponseBody<Data>, ErrorResponseBody, Params, Context>,
  "onError"
> & {
  errorModalDisabled?: boolean
  getFirst?: GetFirst
  onError?: (error: ResError<GetFirst>, variables: Params, context: Context | undefined) => Promise<unknown> | unknown
  onRawError?: UseMutationOptions<ResponseBody<Data>, ErrorResponseBody, Params, Context>["onError"]
}

type ResError<GetFirst extends boolean> = GetFirst extends true ? string | null : FormattedValidationError[]

type Result<Data, GetFirst extends boolean, Params = void, Context = unknown> = UseMutationResult<
  ResponseBody<Data>,
  ErrorResponseBody,
  Params,
  Context
> & {
  resError: ResError<GetFirst>
}

const useCustomMutation = <Data, GetFirst extends boolean, Params = void, Context = unknown>({
  onError,
  onRawError,
  getFirst,
  errorModalDisabled,
  ...params
}: Props<Data, GetFirst, Params, Context>): Result<Data, GetFirst, Params, Context> => {
  const [errors, setErrors] = useState<FormattedValidationError[]>([])
  const modal = useModal()
  const mutation = useMutation({
    onMutate: () => {
      setErrors([])
    },
    onError: (error: ErrorResponseBody, vars: Params, context: Context | undefined) => {
      const formattedErrors =
        error.code === HttpStatusCode.UnprocessableEntity
          ? formatErrorsFromResponse(error.errors)
          : [{ name: [0], errors: [error.message] }]

      setErrors(formattedErrors)

      const errorParam = (getFirst ? getFirstError(formattedErrors) : formattedErrors) as ResError<GetFirst>

      if (!errorModalDisabled) {
        modal.error({
          content: (
            <div>
              {Array.isArray(errorParam) ? (
                errorParam.map((err) => <div key={err.name.join("-")}>{err.errors.join("、")}</div>)
              ) : (
                <span>{errorParam}</span>
              )}
            </div>
          ),
        })
      }

      if (onError) {
        return onError(errorParam, vars, context)
      }

      if (onRawError) {
        return onRawError(error, vars, context)
      }
    },
    ...params,
  })

  return useMemo(() => {
    const resError = (getFirst ? getFirstError(errors) : errors) as ResError<GetFirst>

    return { resError, ...mutation }
  }, [errors, mutation])
}

export { useCustomMutation }

export type { Props as CustomMutationProps }
